import React, {
  useState, useEffect, useRef, useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { useChangeModalPosition, usePortalElement } from '../../../Modal/hooks';
import ModalContext from './ModalContext';
import Header from './Header';
import Body from './Body';
import Footer from './Footer';
import useDontShowAgainMessageRead from '../../../DontShowAgain/useDontShowAgainMessageRead';
import DontShowAgain from '../../../DontShowAgain/DontShowAgain';
import useModalContext from '../../../ModalContext/useModalContext';
import GenericItem from './GenericItem';
import ConfirmItem from './ConfirmItem';
import FeedbackItem from './FeedbackItem';
import { createClosedByDontShowAgainEvent, isClosedByDontShowAgainEvent } from './utils';
import useComponentTimeout from '../../Hooks/useComponentTimeout';

/**
Visualizza un contenuto che blocca temporaneamente le interazioni con il resto dell'interfaccia.

Si può utilizzare per questi tipi di messaggi:

- Chiedere conferma o fare decisioni
- Fornire delle informazioni
- Completare dei compiti importanti all'interno di un processo

La modale è creata a partire da un elemento figlio del body della pagina.

_Nota_: Se la modale non contiene delle azioni (ad es. Salva),
ma contiene ad es. un processo che richiede i pulsanti "Indietro" e "Avanti",
bisogna inserire il pulsante "X" per poterla chiudere.

_Esempio struttura del componente_

```js
<Modal ... >
  <Modal.Header>
    ...
  </Modal.Header>

  <Modal.Body>
    ...
  </Modal.Body>

  <Modal.Footer>
    ...
  </Modal.Footer>
</Modal>
```

E' inoltre possibile definire una serie di contenuti di una modale che verranno presentati come un gruppo unico.

Ogni componente manterrà il suo stato e verrà distrutto solo alla chiusura della modale.

La proprietà `current` definisce l'elemento presentato.

_Esempio struttura del componente_

```js
<Modal current="modale1" ... >
  <Modal.Generic id="modale1" >
    ...
  </Modal.Generic>

  <Modal.Confirm
    id="modale2"
    ...
  />

  <Modal.Feedback
    id="modale3"
    ...
  />
</Modal>
```

  */
export const Modal = ({
  show, doNotFocus, className, contentClassName, children,
  dontShowAgainId, dontShowAgainMessage,
  onClose, current, transitionDuration,
  initialPos, style, portalElementRef,
  ...others
}) => {
  const contentRef = useRef();
  useModalContext(show);
  const {
    x, y, overflow, maxHeight, dragging, onMouseDown,
  } = useChangeModalPosition(initialPos, contentRef, portalElementRef, 260);
  const messageRead = useDontShowAgainMessageRead(dontShowAgainId, show);

  useEffect(() => {
    if (show && !doNotFocus && contentRef.current) contentRef.current.focus();
  }, [doNotFocus, show]);

  const [changing, setChanging] = useState({ state: false, current });
  const setComponentTimeout = useComponentTimeout();
  useEffect(() => {
    if (changing.current !== current && !changing.state) {
      setChanging((s) => ({ ...s, state: true }));
      setComponentTimeout(() => setChanging({ state: false, current }), transitionDuration);
    }
  }, [transitionDuration, changing, current, setComponentTimeout]);

  const modalContext = useMemo(() => ({
    overflow, maxHeight, dragging, onMouseDown, current: changing.current,
  }), [changing, dragging, maxHeight, onMouseDown, overflow]);

  if (!show) return null;
  if (messageRead) {
    onClose && onClose(createClosedByDontShowAgainEvent(undefined, dontShowAgainId));
    return null;
  }

  let child = children;
  if (onClose || dontShowAgainId) {
    // modale singola
    child = (
      <GenericItem id={current} onClose={onClose} dontShowAgainId={dontShowAgainId} dontShowAgainMessage={dontShowAgainMessage}>
        {children}
      </GenericItem>
    );
  }
  return (
    <div role="dialog" className={cns('v2-modal', className)}>
      <div className="v2-modal__overlay" />
      <div
        style={{
          marginLeft: x,
          marginTop: y,
          transition: `opacity ${transitionDuration}ms`,
          ...style,
        }}
        tabIndex="-1"
        ref={contentRef}
        className={cns('v2-modal__content', { 'v2-modal__content--changing': changing.state }, contentClassName)}
        {...others}
      >
        <ModalContext.Provider value={modalContext}>
          {child}
        </ModalContext.Provider>
      </div>
    </div>
  );
};

Modal.propTypes = {
  /** Contenuto da renderizzare all'interno del componente.
   * Può essere di tipo `Modal.Header`, `Modal.Body` o `Modal.Footer` se la modale è singola,
   * altrimenti `Modal.Generic`, `Modal.Confirm` o `Modal.Feedback` .
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * Identificativo della modale corrente. Se è definita una solo modale, lasciare non definito.
   */
  current: PropTypes.string,
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Class css dal aggiungere a content */
  contentClassName: PropTypes.string,
  /** Indica se aprire la modale non in focus */
  doNotFocus: PropTypes.bool,
  /** Indica la posizione iniziale di apertura della modale */
  initialPos: PropTypes.shape({
    x: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    y: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  /** Indica se mostrare o meno la modale */
  show: PropTypes.bool,
  /** stile personalizzabile da aggiungere al componente */
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types

  /**
Callback di chiusura della modale.

Il primo parametro è l'evento scatenante.

Se la chiusura avviene a seguito della gestione dontShowAgain, ritorna un oggetto con `id` pari all'id della modale e
`dontShowAgainId` pari alla prop `dontShowAgainId` della modale.
Per verificate che la chiusura sia conseguenza della gestione dontShowAgain, usare il metodo `Modal.isClosedByDontShowAgainEvent`.

_Da utilizzare solo nel caso di modale unica_
   */
  onClose: PropTypes.func,
  /**
Identificativo del componente per la gestione 'Non visualizzare più'. Se impostato abilita la gestione.
Lo stato è salvato nella sessione locale del browser quindi è necessario un identificativo per gestire il salvataggio.

Tipicamente è composto dall'id dell'utente e da un identificativo del messaggio.

Utilizzare `Modal.clearMessageStatus` per pulire lo stato.

_Da utilizzare solo nel caso di modale unica_
*/
  dontShowAgainId: PropTypes.string,

  /**
Messaggio del componente per la gestione 'Non visualizzare più'.

_Da utilizzare solo nel caso di modale unica_
*/
  dontShowAgainMessage: PropTypes.string,

  /**
Millisec della durata della transazione al cambio di modale.
*/
  transitionDuration: PropTypes.number,

  /** USO INTERNO. Reference del nodo */
  // eslint-disable-next-line react/forbid-prop-types
  portalElementRef: PropTypes.object.isRequired,
};

Modal.defaultProps = {
  children: null,
  className: undefined,
  contentClassName: '',
  doNotFocus: false,
  onClose: undefined,
  show: false,
  style: {},

  dontShowAgainId: undefined,
  dontShowAgainMessage: 'Non mostrare più questo messaggio',
  initialPos: {
    x: 'auto',
    y: (window.innerHeight * 0.05),
  },
  current: '__only_one_child__',
  transitionDuration: 500,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default function ModalPortal(props) {
  // uso questo wrapper perchè altrimenti storybook non estrae le props se si usa createPortal!!!
  const portalElementRef = usePortalElement(undefined, 'modal-element-v2');
  return createPortal(
    <Modal {...props} portalElementRef={portalElementRef} />,
    portalElementRef.current,
  );
}

ModalPortal.Header = Header;
ModalPortal.Body = Body;
ModalPortal.Footer = Footer;
ModalPortal.Generic = GenericItem;
ModalPortal.Confirm = ConfirmItem;
ModalPortal.Feedback = FeedbackItem;

ModalPortal.clearMessageStatus = DontShowAgain.clearMessageStatus;
ModalPortal.isDontShowAgainOptionSelected = DontShowAgain.isDontShowAgainOptionSelected;
ModalPortal.isClosedByDontShowAgainEvent = isClosedByDontShowAgainEvent;
